// Helpers for turning a user's stored `steamLink` (a free-text profile URL) or a
// pasted value into an identifier the CS2KZ API accepts.
//
// The CS2KZ API accepts BOTH a 64-bit SteamID ("76561198…") and a legacy
// SteamID ("STEAM_1:0:…") in its `player=` param and /players/{player} route,
// so we never need to convert between the two — we just normalise to one of
// those two forms and hand it straight to the API.
//
// Vanity URLs ("steamcommunity.com/id/<name>") CANNOT be resolved in the
// browser (Steam's resolution endpoints send no CORS headers), so those return
// null and the UI falls back to asking the user to paste their SteamID. A
// future server-side resolver (n8n) can fill that gap — see MapOfTheWeek docs.

const STEAMID64_RE = /\b(7656119\d{10})\b/;
const LEGACY_STEAMID_RE = /\bSTEAM_[0-5]:[01]:\d+\b/i;

// Normalise a legacy SteamID to the universe-1 form the CS2KZ API emits
// (STEAM_1:Y:Z). Steam sometimes renders universe 0 (STEAM_0:...); the account
// component (Y:Z) is what actually matters, so we just force the "1".
const normaliseLegacy = (value) => value.replace(/^STEAM_[0-5]:/i, 'STEAM_1:').toUpperCase();

/**
 * Parse a Steam profile URL or raw identifier into a CS2KZ-ready SteamID.
 * @param {string} input a profile URL, a SteamID64, or a legacy STEAM_ id
 * @returns {{ steamId: string } | { unresolvable: true } | null}
 *   - `{ steamId }` when we could extract an id the API accepts
 *   - `{ unresolvable: true }` for a vanity `/id/<name>` URL (needs a server)
 *   - `null` when the input is empty or unrecognised
 */
export const parseSteamId = (input) => {
  const value = (input || '').trim();
  if (!value) return null;

  const legacy = value.match(LEGACY_STEAMID_RE);
  if (legacy) return { steamId: normaliseLegacy(legacy[0]) };

  const id64 = value.match(STEAMID64_RE);
  if (id64) return { steamId: id64[1] };

  // A vanity profile URL — we have the name but can't resolve it client-side.
  if (/steamcommunity\.com\/id\//i.test(value)) return { unresolvable: true };

  return null;
};

/** True if a pasted value looks like something parseSteamId can use. */
export const isLinkableSteamValue = (input) => {
  const parsed = parseSteamId(input);
  return !!(parsed && parsed.steamId);
};
